import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from "@tanstack/react-router";
import { DashboardLayout } from "./components/layout/DashboardLayout";
import { SettingsLayout } from "./components/layout/SettingsLayout";
import { requireAdmin, requireAuth, requireMgrOrAdmin } from "./lib/route-guards";
import { RootLayout } from "./routes/__root";
import { ConfigurationHistoryPage } from "./routes/dashboard/configuration-history";
import { HistoryPage } from "./routes/dashboard/history";
import { DashboardPage } from "./routes/dashboard/index";
import { ItemsPage } from "./routes/dashboard/items";
import { ItemDetailPage } from "./routes/dashboard/items/detail";
import { LogTimePage } from "./routes/dashboard/log-time";
import { OverheadCategoriesPage } from "./routes/dashboard/overhead-categories";
import { OverheadCategoryDetailPage } from "./routes/dashboard/overhead-categories.$categoryId";
import { ProfilePage } from "./routes/dashboard/profile";
import { SettingsCategoriesPage } from "./routes/dashboard/settings/categories";
import { SettingsCategoryDetailPage } from "./routes/dashboard/settings/categories.$categoryId";
import { SettingsJobTypesPage } from "./routes/dashboard/settings/job-types";
import { SettingsSystemPage } from "./routes/dashboard/settings/system";
import { SettingsUsersPage } from "./routes/dashboard/settings/users";
import { WorkOrdersPage } from "./routes/dashboard/work-orders";
import { LoginPage } from "./routes/login";
import { SignupPage } from "./routes/signup";

const rootRoute = createRootRoute({ component: RootLayout });

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  beforeLoad: () => {
    throw redirect({ to: "/login" });
  },
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  validateSearch: (search: Record<string, unknown>) => ({
    reason: typeof search.reason === "string" ? search.reason : undefined,
  }),
  component: LoginPage,
});

const signupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/signup",
  component: SignupPage,
});

function DashboardLayoutRoute() {
  return (
    <DashboardLayout>
      <Outlet />
    </DashboardLayout>
  );
}

const dashboardLayout = createRoute({
  getParentRoute: () => rootRoute,
  path: "/dashboard",
  beforeLoad: requireAuth,
  component: DashboardLayoutRoute,
});

const dashboardIndex = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/",
  component: DashboardPage,
});

const logTimeRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/log-time",
  component: LogTimePage,
});

const historyRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/history",
  component: HistoryPage,
});

const workOrdersRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/work-orders",
  beforeLoad: requireAdmin,
  component: WorkOrdersPage,
});

const itemsRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/items",
  component: ItemsPage,
});

const itemDetailRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/items/$itemId",
  component: ItemDetailPage,
});

function SettingsLayoutRoute() {
  return (
    <SettingsLayout>
      <Outlet />
    </SettingsLayout>
  );
}

const settingsLayout = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/settings",
  beforeLoad: async (ctx) => {
    await requireAdmin(ctx);
    if (ctx.location.pathname === "/dashboard/settings") {
      throw redirect({ to: "/dashboard/settings/categories" });
    }
  },
  component: SettingsLayoutRoute,
});

const settingsUsersRoute = createRoute({
  getParentRoute: () => settingsLayout,
  path: "/users",
  component: SettingsUsersPage,
});

const settingsSystemRoute = createRoute({
  getParentRoute: () => settingsLayout,
  path: "/system",
  component: SettingsSystemPage,
});

const settingsCategoriesRoute = createRoute({
  getParentRoute: () => settingsLayout,
  path: "/categories",
  component: SettingsCategoriesPage,
});

const settingsCategoryDetailRoute = createRoute({
  getParentRoute: () => settingsLayout,
  path: "/category/$categoryId",
  component: SettingsCategoryDetailPage,
});

const settingsJobTypesRoute = createRoute({
  getParentRoute: () => settingsLayout,
  path: "/job-types",
  component: SettingsJobTypesPage,
});

const configHistoryRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/configuration-history",
  beforeLoad: requireMgrOrAdmin,
  component: ConfigurationHistoryPage,
});

const overheadCategoriesRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/overhead-categories",
  beforeLoad: requireMgrOrAdmin,
  component: OverheadCategoriesPage,
});

const overheadCategoryDetailRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/overhead-categories/$categoryId",
  beforeLoad: requireMgrOrAdmin,
  component: OverheadCategoryDetailPage,
});

const profileRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/profile",
  component: ProfilePage,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  signupRoute,
  dashboardLayout.addChildren([
    dashboardIndex,
    logTimeRoute,
    historyRoute,
    workOrdersRoute,
    itemsRoute,
    itemDetailRoute,
    settingsLayout.addChildren([
      settingsUsersRoute,
      settingsSystemRoute,
      settingsCategoriesRoute,
      settingsCategoryDetailRoute,
      settingsJobTypesRoute,
    ]),
    configHistoryRoute,
    overheadCategoriesRoute,
    overheadCategoryDetailRoute,
    profileRoute,
  ]),
]);

export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
